import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import Select from "react-select";
import NotyfContext from "../contexts/NotyfContext.js";
import { getWhatsAppRegistrationOptions } from "../api/whatsapp.js";
import { WHATSAPP_REGISTRATION_USER_TYPES, getWhatsAppRegistrationField, copyWhatsAppRegistrationField,
  isRegistrationWhatsAppTemplate, getRegistrationCustomVariables } from "../utils/whatsappRegistration.js";

export default function WhatsAppRegistrationFields({ eventId, values, setFieldValue, fields = [], fieldsUsers = [], setDataAddForm }) {
  const notyf = useContext(NotyfContext);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const register = values.json?.whatsapp?.register || {};
  const phone = getWhatsAppRegistrationField(fields, fieldsUsers, register);
  const canAddPhone = Boolean(phone.missingForm && phone.source && typeof setDataAddForm === "function");
  // Adds the phone field (same inputID) to the composition an enabled user type still lacks.
  const addPhoneToMissingForm = () => {
    if (!canAddPhone) return;
    const form = phone.missingForm;
    setDataAddForm((previous) => {
      const list = Array.isArray(previous?.[form]) ? previous[form] : [];
      const index = list.findIndex((field) => String(field.inputID) === String(phone.source.inputID));
      return { ...previous, [form]: index === -1
        ? [...list, copyWhatsAppRegistrationField(phone.source, form)]
        : list.map((field, position) => position === index ? copyWhatsAppRegistrationField(field, form) : field) };
    });
  };
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(""); setOptions(null);
    getWhatsAppRegistrationOptions({ eventId, signal: controller.signal }).then(({ data }) => {
      if (!controller.signal.aborted) setOptions(data);
    }).catch((err) => {
      if (!controller.signal.aborted) setError(err.response?.data?.message || "Não foi possível carregar os modelos de WhatsApp.");
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [eventId]);

  useEffect(() => {
    // A composition missing for an enabled type is fixed below (add the field or
    // turn the type off); the save validation blocks it meanwhile.
    if (register.status && !phone.field && !phone.missingForm) {
      setFieldValue("json.whatsapp.register.status", false);
      notyf?.open({ type: "warning", message: `O envio automático de WhatsApp foi desativado. ${phone.error}` });
    }
  }, [register.status, phone.field, phone.missingForm, phone.error, setFieldValue, notyf]);

  const templates = useMemo(() => [...(options?.templates || []), ...(options?.selected_templates || [])], [options]);
  const enabledTypes = WHATSAPP_REGISTRATION_USER_TYPES.filter(({ type }) => (
    register.templates?.[String(type)]?.enabled !== false
  ));
  const templatesReady = enabledTypes.every(({ type }) => {
    const reference = register.templates?.[String(type)] || {};
    return templates.some((template) => Number(template.template_id) === Number(reference.template_id)
      && Number(template.template_version || template.active_version) === Number(reference.template_version)
      && isRegistrationWhatsAppTemplate(template, type));
  });
  const canEnable = Boolean(phone.field && !loading && !error && options?.schema_ready === true
    && options?.default_sender_id && templatesReady && enabledTypes.length > 0
    && (!register.sender_id || Number(register.sender_id) === Number(options.default_sender_id)));
  const setReference = (type, next) => setFieldValue("json.whatsapp.register.templates", {
    ...(register.templates || {}), [String(type)]: { ...(register.templates?.[String(type)] || {}), ...next },
  });
  const toggle = () => {
    if (!register.status && !canEnable) return;
    if (!register.sender_id && options?.default_sender_id) setFieldValue("json.whatsapp.register.sender_id", options.default_sender_id);
    setFieldValue("json.whatsapp.register.status", !register.status);
  };

  return <Form.Group className="mb-0">
    <Form.Check checked={register.status === true} type="checkbox" name="json.whatsapp.register.status"
      label="Envio automático de WhatsApp após registro" onChange={toggle}
      disabled={!register.status && !canEnable} />
    {!phone.field && <Form.Text className="text-muted d-block">{phone.error}</Form.Text>}
    {canAddPhone && <Button variant="link" size="sm" className="p-0" onClick={addPhoneToMissingForm}>
      {phone.missingForm === "fieldsUsers" ? "Adicionar o campo ao formulário dos usuários gestores" : "Adicionar o campo ao formulário dos participantes"}
    </Button>}
    {loading && <div className="small text-muted mt-2"><Spinner animation="border" size="sm" className="me-2" />Carregando modelos de WhatsApp…</div>}
    {error && <Alert variant="danger" className="mt-2 mb-0">{error}</Alert>}
    {options?.schema_ready === false && <Alert variant="info" className="mt-2 mb-0">A atualização do banco para envio automático de WhatsApp ainda precisa ser aplicada.</Alert>}
    {(phone.field || phone.missingForm) && options?.schema_ready === true && <div className="border p-3 mt-2">
      <Form.Group className="mb-3">
        <Form.Label>Remetente de WhatsApp</Form.Label>
        <Form.Select value={register.sender_id || ""} onChange={(event) => setFieldValue("json.whatsapp.register.sender_id", Number(event.target.value) || null)}>
          <option value="">Selecione um remetente…</option>
          {options?.default_sender_id && <option value={options.default_sender_id}>{options.sender_label}</option>}
          {register.sender_id && Number(register.sender_id) !== Number(options?.default_sender_id) && <option value={register.sender_id}>Remetente anterior indisponível</option>}
        </Form.Select>
      </Form.Group>
      <Row className="g-3">
        {WHATSAPP_REGISTRATION_USER_TYPES.map(({ type, label }) => {
          const reference = register.templates?.[String(type)] || {};
          const items = templates.filter((template) => isRegistrationWhatsAppTemplate(template, type))
            .filter((template, index, all) => all.findIndex((other) => Number(other.template_id) === Number(template.template_id)
              && Number(other.template_version || other.active_version) === Number(template.template_version || template.active_version)) === index)
            .map((template) => ({ value: `${template.template_id}:${template.template_version || template.active_version}`,
              label: `${template.internal_title} · versão ${template.template_version || template.active_version}`, template }));
          const selected = items.find((item) => item.value === `${reference.template_id}:${reference.template_version}`);
          const fieldOptions = (type === 0 ? fields : fieldsUsers).filter((field) => field.inputID && ["input","textarea","select","checkbox","radio","date","number"].includes(field.type));
          const customVariables = getRegistrationCustomVariables(selected?.template);
          return <Col key={type} xs={12} lg={4}>
            <Form.Check type="checkbox" checked={reference.enabled !== false} label={`Enviar para ${label}`}
              onChange={(event) => setReference(type, { enabled: event.target.checked })} className="mb-2" />
            <Select options={items} isClearable={false} isDisabled={reference.enabled === false}
              placeholder={`Selecione um modelo para ${label.toLowerCase()}…`}
              value={selected || (reference.template_id ? { value: "unavailable", label: `Modelo #${reference.template_id} · versão ${reference.template_version} indisponível` } : null)}
              onChange={(item) => setReference(type, { template_id: item?.template.template_id || null,
                template_version: item?.template.template_version || item?.template.active_version || null, template_variables: {} })}
              noOptionsMessage={() => "Nenhum modelo global aprovado compatível."} />
            {reference.enabled !== false && customVariables.map((name) => <Form.Group key={name} className="mt-2">
              <Form.Label className="small">Campo para {`{{${name}}}`}</Form.Label>
              <Form.Select value={reference.template_variables?.[name]?.input_id || ""} onChange={(event) => setReference(type, {
                template_variables: { ...(reference.template_variables || {}), [name]: { source: "event_field", input_id: event.target.value } },
              })}><option value="">Selecione um campo…</option>{fieldOptions.map((field) => <option key={field.inputID} value={field.inputID}>{field.label}</option>)}</Form.Select>
            </Form.Group>)}
          </Col>;
        })}
      </Row>
    </div>}
  </Form.Group>;
}
